import type { InventoryFormValues, InventoryLookups } from '../../types/inventoryForm.types';

interface StockPricingSectionProps {
    values: InventoryFormValues;
    stockValue: number;
    lookups?: InventoryLookups;
    onChange: <K extends keyof InventoryFormValues>(key: K, value: InventoryFormValues[K]) => void;
    isEdit?: boolean;
}

function FieldLabel({ text, required = false }: { text: string; required?: boolean }) {
    return (
        <label className="mb-2 block sm:text-sm text-xs text-slate-700">
            {text}
            {required ? <span className="text-red-500"> *</span> : null}
        </label>
    );
}

export function StockPricingSection({ values, stockValue, lookups, onChange, isEdit }: StockPricingSectionProps) {
    return (
        <div className="rounded-md border border-slate-200 bg-white p-3 space-y-4">
            <h3 className="sm:text-sm text-xs text-slate-900 border-b border-slate-100 pb-2">Giá và Tồn kho</h3>
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <div className="space-y-1">
                    <FieldLabel text="Giá nhập gần nhất (₫)" />
                    <input
                        type="number"
                        min={0}
                        value={values.unit_price}
                        onChange={(event) => onChange('unit_price', Number(event.target.value || 0))}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="0"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Giá trị tồn kho (₫)" />
                    <input
                        value={stockValue.toLocaleString('vi-VN')}
                        readOnly
                        className="h-10 w-full rounded-md border border-slate-200 bg-slate-50 px-3 sm:text-sm text-xs text-slate-700"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Số lượng tồn đầu" />
                    <input
                        type="number"
                        min={0}
                        value={values.opening_stock}
                        onChange={(event) => onChange('opening_stock', Number(event.target.value || 0))}
                        disabled={isEdit}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
                        placeholder="0"
                    />
                </div>

                <div className="space-y-1">
                    <FieldLabel text="Vị trí lưu kho" />
                    <input
                        value={values.storage_location}
                        onChange={(event) => onChange('storage_location', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="VD: Kệ A1, Ngăn 2"
                    />
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Kho mặc định" />
                    <select
                        value={values.warehouse_id}
                        onChange={(event) => onChange('warehouse_id', event.target.value)}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                    >
                        <option value="">Chọn kho</option>
                        {(lookups?.warehouseOptions ?? []).map((option, index) => (
                            <option key={`${option.value}-${index}`} value={option.value}>{option.label}</option>
                        ))}
                    </select>
                </div>
                <div className="space-y-1">
                    <FieldLabel text="Mức tồn tối thiểu" />
                    <input
                        type="number"
                        min={0}
                        value={values.minimum_stock}
                        onChange={(event) => onChange('minimum_stock', Number(event.target.value || 0))}
                        className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 sm:text-sm text-xs focus:border-emerald-500 focus:outline-none"
                        placeholder="0"
                    />
                </div>
            </div>

            <div className="mt-2 border-t border-slate-50 pt-3 flex items-center gap-3">
                <input
                    id="track-stock"
                    type="checkbox"
                    checked={values.is_stock_tracked}
                    onChange={(event) => onChange('is_stock_tracked', event.target.checked)}
                    className="w-4 h-4 rounded text-emerald-600 focus:ring-emerald-500"
                />
                <div className="flex flex-col">
                    <label htmlFor="track-stock" className="sm:text-sm text-xs text-slate-900 cursor-pointer">Theo dõi tồn kho</label>
                    <p className="text-xs text-slate-500">Tự động cộng/trừ số lượng khi có phiếu nhập/xuất kho</p>
                </div>
            </div>
        </div>
    );
}
